import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import '../styles/DailyCheckIn.css';

const DailyCheckIn = () => {
  const { isAuthenticated } = useContext(AuthContext);
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchStreakData(true); // Show loading only on initial mount
    }
  }, [isAuthenticated]);

  const fetchStreakData = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/daily-rewards/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStreakData(response.data.data);
    } catch (err) {
      console.error('Error fetching streak data:', err);
      setError('Failed to load daily check-in data');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      setClaiming(true);
      setError('');
      setSuccessMessage('');

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/daily-rewards/check-in', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage(response.data.message);
      
      // Refresh data (silent)
      await fetchStreakData(false);
      
      // Trigger a custom event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('dailyCheckInComplete'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error checking in:', err);
      setError(err.response?.data?.message || 'Failed to check in');
    } finally {
      setClaiming(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="daily-checkin-widget guest">
        <div className="checkin-header">
          <h3>🔥 Daily Check-In</h3>
          <p>Login to start earning streak rewards!</p>
        </div>
        <Link to="/login" className="checkin-button">
          Log In to Claim
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="daily-checkin-widget loading">
        <div className="spinner-small"></div>
      </div>
    );
  }

  if (error && !streakData) {
    return (
      <div className="daily-checkin-widget error">
        <p>{error}</p>
        <button onClick={fetchStreakData} className="retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="daily-checkin-widget">
      <div className="checkin-header">
        <h3>🔥 Daily Check-In</h3>
        <div className="streak-info">
          <span className="streak-number">{streakData?.currentStreak || 0}</span>
          <span className="streak-label">Day Streak</span>
        </div>
      </div>

      <div className="streak-visual">
        {[...Array(7)].map((_, index) => (
          <div 
            key={index}
            className={`streak-day ${index < (streakData?.currentStreak || 0) % 7 ? 'active' : ''}`}
          >
            <div className="day-icon">
              {index < (streakData?.currentStreak || 0) % 7 ? '🔥' : '⚪'}
            </div>
            <div className="day-label">Day {index + 1}</div>
          </div>
        ))}
      </div>

      {successMessage && (
        <div className="success-message">
          <span className="success-icon">✅</span>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
        </div>
      )}

      <button
        onClick={handleCheckIn}
        disabled={!streakData?.canCheckIn || claiming}
        className={`checkin-button ${!streakData?.canCheckIn ? 'disabled' : ''}`}
      >
        {claiming ? (
          <>
            <span className="spinner-icon">⏳</span>
            Claiming...
          </>
        ) : streakData?.canCheckIn ? (
          <>
            <span className="coin-icon">💰</span>
            Claim Daily Coins
          </>
        ) : (
          <>
            <span className="check-icon">✓</span>
            Already Claimed Today
          </>
        )}
      </button>

      <div className="checkin-stats">
        <div className="stat-item">
          <span className="stat-label">Longest Streak</span>
          <span className="stat-value">{streakData?.longestStreak || 0} days</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Total Check-ins</span>
          <span className="stat-value">{streakData?.totalCheckIns || 0}</span>
        </div>
      </div>

      <div className="next-milestone">
        {streakData?.nextMilestone && (
          <div className="milestone-info">
            <span className="milestone-icon">🎯</span>
            <div className="milestone-text">
              <strong>Next Reward:</strong> {streakData.nextMilestone.name}
              <br />
              <small>{streakData.nextMilestone.streak - streakData.currentStreak} days to go</small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyCheckIn;
